<template>
  <div :class="$b({ isEmailSignup })">
    <StructuredText
      :data="content"
      :render-block="renderBlock"
      :custom-rules="customRules"
    />
  </div>
</template>

<script>
import { StructuredText, renderRule } from 'vue-datocms'
import { isHeading, isParagraph, isLink } from 'datocms-structured-text-utils'

import {
  TextBackground,
  SingleImage,
  HighlightHeading,
  DialogStack,
  DialogComponent,
  DialogTextSmall,
  DataCaptureBlock,
  VisionButton,
  VisionLink,
} from '#components'

export default {
  name: 'DialogContent',

  components: {
    StructuredText,
  },

  props: {
    content: {
      type: Object,
      required: true,
    },
    isEmailSignup: {
      type: Boolean,
      default: false,
    },
    hasPromoTheme: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    customRules() {
      return [
        renderRule(
          isHeading,
          ({ adapter: { renderNode: h }, node, children, key }) => {
            if (node.level === 1) return h(`h${node.level}`, { key }, children)
            return h(`h${node.level}`, { key }, children)
          }
        ),
        renderRule(
          isParagraph,
          ({ adapter: { renderNode: h }, children, key }) => {

            if (children[0] === '' && children.length === 1) return null

            return h('p', { key }, children)
          }
        ),
        renderRule(
          isLink,
          ({ adapter: { renderNode: h }, node, children, key }) => {
            return h(
              VisionLink,
              { key, to: node.url, lineLink: true, display: 'inline-block' },
              children
            )
          }
        ),
      ]
    },
  },

  methods: {
    getInlineCtaConfig(record) {
      switch (record.style) {
        case 'PrimaryButton':
          return {
            component: VisionButton,
            backgroundColour: 'gradient',
            textColour: '',
            outline: false,
            promoButton: this.hasPromoTheme
          }
        case 'SecondaryButton':
          return {
            component: VisionButton,
            backgroundColour: 'black',
            textColour: 'white',
            outline: false,
          }
        case 'TertiaryButton':
          return {
            component: VisionButton,
            backgroundColour: 'white',
            textColour: 'black',
            outline: false,
          }
        case 'OutlineButton':
          return {
            component: VisionButton,
            backgroundColour: 'white',
            textColour: 'white',
            outline: true,
          }
        case 'PrimaryLineLink':
          return {
            component: VisionLink,
            theme: 'gradient'
          }
        case 'SecondaryLineLink':
          return {
            component: VisionLink,
            theme: 'white'
          }
      }
    },

    renderBlock({ record }) {
      const isFirst =
        this.content?.value?.document?.children?.findIndex(
          (item) => item?.type === 'block' && item?.item === record.id
        ) === 0

      const $emit = this.$emit // We need to ref this where it might not be available

      const widthSuffix = record.widthMode === 'percentage' ? '%' : 'px'

      switch (record.__typename) {
        case 'SingleImageRecord':
          return h(SingleImage, {
            class: { 'is-first': isFirst, 'is-block': true },
            image: record.image,
            maxWidth: `${record.maxWidth}${widthSuffix}`,
            minWidth: record.minWidth ? `${record.minWidth}${widthSuffix}` : undefined,
          })
        case 'TextWithBackgroundRecord':
          return h(TextBackground, {
            class: { 'is-first': isFirst },
            id: record.id,
            title: record.title,
            subtitle: record.subtitle,
            copy: record.copy,
            background: record.background,
            mobileBackground: record.mobileBackground,
            gradientOverlay: record.gradientOverlay,
          })
        case 'HighlighterHeadingRecord':
          return h(HighlightHeading, {
            heading: record,
            background: 'light',
          })
        case 'DialogStackRecord':
          return h(DialogStack, {
            heading: record.heading,
            subheading: record.subheading,
          })
        case 'DialogComponentRecord':
          return h(DialogComponent, {
            componentName: record.componentName,
          })
        case 'TextSmallRecord':
          return h(DialogTextSmall, {
            text: record.text,
          })
        case 'InlineDataCaptureFormRecord':
          return h(DataCaptureBlock, {
            block: record.block,
            inModal: true,
            promoTheme: this.hasPromoTheme,
            onClose() {
              $emit('close')
            },
          })
        case 'InlineCtaRecord': {
          const ctaConfig = this.getInlineCtaConfig(record)

          if (ctaConfig.component === VisionButton) {
            return h(
              ctaConfig.component,
              {
                backgroundColour:
                ctaConfig.backgroundColour,
                textColour: ctaConfig.textColour,
                square: false,
                outline: ctaConfig.outline,
                to: record.target,
                onClick() {
                  if (record.target?.startsWith('@')) {
                    $emit(record.target.replace('@', '')) // Emit the event name
                  }
                },
              },
              {
                content: () => record.copy,
              }
            )
          } else if (ctaConfig.component === VisionLink) {
            return h(
              ctaConfig.component,
              {
                theme: ctaConfig.theme,
                to: record.target,
                lineLink: true,
                display: 'inline-block',
                onClick() {
                  if (record.target?.startsWith('@')) {
                    $emit(record.target.replace('@', '')) // Emit the event name
                  }
                },
              },
              () => record.copy
            )
          }
        }
          break;
        default: {
          return null
        }
      }
    },
  },
}
</script>

<style lang="postcss">
.DialogContent {
  h1 {
    @apply heading-1 mb-12;
  }

  h2 {
    @apply heading-2 mb-8;
  }

  h3 {
    @apply heading-3 mb-6;
  }

  h4 {
    @apply heading-4 mb-4;
  }

  h5 {
    @apply heading-5 mb-3;
  }

  p {
    @apply text-sm mt-3 mb-3;

    + .TextBackground {
      @apply mt-10;
    }
  }

  ul {
    @apply list-disc;
  }

  ol {
    @apply list-decimal;
  }

  ul,
  ol {
    @apply mt-3 mb-6;

    li {
      @apply ml-4 mb-2;

      p {
        @apply my-0;
      }
    }
  }

  p,
  a {
    + h1 {
      @apply mt-24;
    }

    + h2 {
      @apply mt-20;
    }

    + h3 {
      @apply mt-16;
    }

    + h4 {
      @apply mt-12;
    }

    + h5 {
      @apply mt-10;
    }
  }

  mark {
    @apply t-gradient;
  }
}
</style>
